////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// grid-math.ts — pure geometry helpers for encounter-map callers.
// ----------------------------------------------------------------------------------------------------
// Bundled into includes/js/dist/encounter-map-bundle.js via
// tools/build/build-encounter-map-bundle.mjs. Imported by ./index.ts which Object.assigns
// these onto window.FTEncounterMap.geom — class methods in template.js delegate via
// 1-line wrappers that preserve the original method signature.
//
// Everything here is pure: no `this`, no DOM, no fetch, no console side effects.
// Inputs in, outputs out — straightforward to type and trivial to reason about.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type Rect = {
    left:   number;
    right:  number;
    top:    number;
    bottom: number;
};

export type Interval = [number, number];

// XZ-axis convention: encounter map points use {x, z}, NOT {x, y}. The Y axis is
// vertical/height; pathing distance only considers the horizontal plane.
export type FT3DPoint = { x: number; z: number };

// Minimal shape the bounds helper accepts. `widget.inputs` carries the
// encounter bounds; nothing else on the widget is touched.
export type ClampInputsCarrier = {
    inputs?: {
        xMin?: unknown;
        xMax?: unknown;
        zMin?: unknown;
        zMax?: unknown;
    };
} | null | undefined;


// SOURCE: includes/js/template.js:28356 (AOEManager._distance).
// Horizontal-plane distance between two points on the encounter map.
export function distance(p1: FT3DPoint, p2: FT3DPoint): number {
    return Math.hypot(p1.x - p2.x, p1.z - p2.z);
}


// SOURCE: includes/js/template.js:22955-22958 (DragManager._rectanglesOverlap).
// True if two axis-aligned footprint rects overlap (matches PHP do_rectangles_overlap).
export function rectanglesOverlap(r1: Rect, r2: Rect): boolean {
    return !(r1.right <= r2.left || r1.left >= r2.right ||
             r1.top <= r2.bottom || r1.bottom >= r2.top);
}


// SOURCE: includes/js/template.js:22943-22952 (DragManager._footprintAabbFromCenterFeet).
export function footprintAabbFromCenterFeet(cx: number, cz: number, w: number, h: number): Rect {
    const hw = w / 2;
    const hh = h / 2;
    return {
        left:   cx - hw,
        right:  cx + hw,
        bottom: cz - hh,
        top:    cz + hh,
    };
}


// SOURCE: includes/js/template.js:22964-22993 (DragManager._widestUncoveredXGap).
// Widest uncovered X-strip of [spanLeft, spanRight] given a set of [a, b] cover intervals.
// JS port of PHP _widest_uncovered_x_gap — used for cross-size sandwich detection.
export function widestUncoveredXGap(spanLeft: number, spanRight: number, intervals: Interval[]): number {
    if (!intervals || intervals.length === 0) return spanRight - spanLeft;

    const clipped: Interval[] = [];
    for (const iv of intervals) {
        const a = Math.max(iv[0], spanLeft);
        const b = Math.min(iv[1], spanRight);
        if (a < b) clipped.push([a, b]);
    }
    if (clipped.length === 0) return spanRight - spanLeft;

    clipped.sort((x, y) => x[0] - y[0]);

    const firstClipped = clipped[0];
    if (!firstClipped) return spanRight - spanLeft;
    const merged: Interval[] = [firstClipped.slice() as Interval];
    for (let i = 1; i < clipped.length; i++) {
        const last = merged[merged.length - 1];
        const current = clipped[i];
        if (!last || !current) continue;
        if (current[0] <= last[1]) {
            last[1] = Math.max(last[1], current[1]);
        } else {
            merged.push(current.slice() as Interval);
        }
    }

    const firstMerged = merged[0];
    if (!firstMerged) return spanRight - spanLeft;
    let widest = firstMerged[0] - spanLeft;
    for (let i = 1; i < merged.length; i++) {
        const current = merged[i];
        const previous = merged[i - 1];
        if (current && previous) widest = Math.max(widest, current[0] - previous[1]);
    }
    const lastMerged = merged[merged.length - 1];
    if (lastMerged) widest = Math.max(widest, spanRight - lastMerged[1]);
    return widest;
}


// SOURCE: includes/js/template.js:22893-22915 (DragManager.clampGridCenterToEncounterVisibleBounds).
// Clamp grid center (x,z) so >= half the width_ft x height_ft footprint stays inside encounter
// bounds (matches PHP clamp_object_center_to_encounter_visible_bounds). The original DragManager
// static took (widget, el, gridX, gridZ) but never used `el`; this signature drops it. The class
// delegate wrapper keeps the original 4-arg signature for byte-identical external API.
export function clampGridCenterToEncounterVisibleBounds(
    widget: ClampInputsCarrier,
    gridX: number,
    gridZ: number,
): { x: number; z: number } {
    if (!widget?.inputs) {
        return { x: gridX, z: gridZ };
    }
    const { xMin, xMax, zMin, zMax } = widget.inputs;
    const xLo = Number.isFinite(Number(xMin)) ? Number(xMin) : -50;
    const xHi = Number.isFinite(Number(xMax)) ? Number(xMax) : 50;
    const zLo = Number.isFinite(Number(zMin)) ? Number(zMin) : -100;
    const zHi = Number.isFinite(Number(zMax)) ? Number(zMax) : 1000;
    let x = gridX;
    if (xLo <= xHi) {
        x = Math.min(xHi, Math.max(xLo, x));
    } else {
        x = (xLo + xHi) / 2;
    }
    let z = gridZ;
    if (zLo <= zHi) {
        z = Math.min(zHi, Math.max(zLo, z));
    } else {
        z = (zLo + zHi) / 2;
    }
    return { x: parseFloat(x.toFixed(3)), z: parseFloat(z.toFixed(3)) };
}
